import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { NavLink, Redirect } from 'react-router-dom'
import queryString from 'query-string'
import { tagHotjarRecording } from '../../helpers/util'
// import useIsMounted (when it's been committed to dev)
import { useIsMounted } from '../../helpers/hooks'
import useAuth from '../../hooks/useAuth'
import useHome from '../../hooks/useHome'
import AppRoutes from '../../routes/appRoutes'
import LoginTransition from '../LoginPage/components/LoginTransition'
import L1NLogoWhiteStroke from '../../svg/L1N-logo-white-stroke.svg'
import InputText from '../../components/InputText/InputText'
import Button from '../../components/Button/Button'
import { useFormik } from 'formik'
import { initialValues, validationSchema } from '../../forms/LoginLinkForm'
import SuccessMessageSvg from '../../svg/SuccessMessage'
import clsx from 'clsx'
import useGlobalLoading from '../../hooks/useGlobalLoading'

const LoginLinkPage = ({ location: { search } }) => {
    const values = search ? queryString.parse(search) : {}
    const { email: emailFromUrl, signature } = values
    const [loginSuccess, setLoginSuccess] = useState(false)
    const [formSuccess, setFormSuccess] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)
    const isMounted = useIsMounted()
    const { handleLoginViaLink, handleLoginSuccess } = useAuth()
    const { handleCreateLoginLink } = useHome()
    const { authenticated, role } = useAuth()
    const { changeLoading } = useGlobalLoading()

    const { handleSubmit, errors, getFieldProps, touched } = useFormik({
        initialValues: initialValues(emailFromUrl),
        validationSchema,
        onSubmit: formValues => {
            submit(formValues)
        },
    })

    const onLoginLinkSuccess = response => {
        // Store session info in local storage
        var session = JSON.stringify(response.data.session)
        localStorage.setItem('session', session)
        handleLoginSuccess(response?.data?.session)
        isMounted.current ? setLoginSuccess(true) : null

        changeLoading(false)
    }

    const onLoginLinkError = () => error => {
        tagHotjarRecording('API Error')
        if (isMounted.current) {
            setLoginSuccess(false)
            // setErrorMessage(
            //     'That login link has expired or was invalid. Please request a new one below.',
            // )
        }
        console.log('error', error.response.data.message)
        changeLoading(false)
    }

    useEffect(() => {
        if (!authenticated && signature !== undefined) {
            isMounted.current ? setFormSuccess(false) : null
            changeLoading(true)
            handleLoginViaLink(values, onLoginLinkSuccess, onLoginLinkError)
        }
    }, [authenticated, signature, tagHotjarRecording])

    const onCreateLinkSuccess = () => {
        isMounted.current ? setFormSuccess(true) : null
        changeLoading(false)
    }

    const onErrorCreateLink = error => {
        const {
            message = 'There was a network error. Please check your internet connection and try again.',
        } = error.response?.data ?? {}
        if (isMounted.current) {
            setErrorMessage(message)
            setFormSuccess(false)
        }
        changeLoading(false)
    }

    const submit = formValues => {
        changeLoading(true)

        handleCreateLoginLink(
            formValues,
            onCreateLinkSuccess,
            onErrorCreateLink,
        )
    }

    const nextPage =
        authenticated && (role === 'admin' || role === 'salesperson') ? (
            <Redirect to={AppRoutes.adminRoutes()} />
        ) : authenticated ? (
            <Redirect to={AppRoutes.dashboard()} />
        ) : (
            <Redirect to={AppRoutes.login()} />
        )

    return loginSuccess ? (
        nextPage
    ) : (
        <LoginTransition>
            <div
                className={clsx(
                    'transition-all w-full sm:p-16 p-6 flex flex-col items-center sm:gap-10 gap-6',
                    formSuccess
                        ? 'justify-between h-full gap-y-20'
                        : 'justify-start',
                )}
            >
                <section className="flex flex-col items-center justify-start w-full">
                    <img className="h-8 w-28" src={L1NLogoWhiteStroke} />
                    <h1 className="sm:text-3xl text-2.5xl mt-4 font-bold text-white">
                        Welcome Back
                    </h1>
                </section>

                {formSuccess ? (
                    <div className="w-full h-auto flex items-center justify-center flex-col gap-y-6 transition-all">
                        <SuccessMessageSvg
                            bg="#42474D"
                            iconColor="#FFC20D"
                            border="#363A3F"
                            hasBorderOpacity={false}
                            className="w-14 h-14"
                        />

                        <p className="text-white font-normal text-base text-center">
                            Your link has been sent.
                            <br />
                            Please check your email shortly.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="w-full h-auto flex flex-col items-center justify-center"
                    >
                        <p className="text-white font-normal text-base mb-6">
                            {
                                "Enter your email below and we'll send you a link you can use to sign in without a password."
                            }
                        </p>
                        <InputText
                            {...getFieldProps('email')}
                            label="Email"
                            variant="black"
                            placeholder=""
                            error={touched['email'] && errors['email']}
                        />
                        <Button
                            className="w-full h-auto mt-1"
                            text="Send Link"
                            variant="secondary"
                        />
                    </form>
                )}

                <NavLink
                    to={AppRoutes.login()}
                    className="text-yellow underline font-normal text-base"
                >
                    Return to Sign In
                </NavLink>

                {errorMessage && (
                    <p className="text-error text-sm font-normal text-center">
                        {errorMessage}
                    </p>
                )}
            </div>
        </LoginTransition>
    )
}

LoginLinkPage.propTypes = {
    authenticated: PropTypes.bool,
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
    role: PropTypes.string,
}

export default LoginLinkPage
